import { useState } from "react";
import { useOne, useUpdate, useList, useGo } from "@refinedev/core";
import { useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  FormField,
  FormHeader,
  FormStickyActions,
  FormSpacer,
  PermissionsSelector,
  FormCard,
  type PermissionOption,
} from "@/components/forms";
import { clearPermissionsCache } from "@/providers/access-control-provider";
import type { Role, RolePayload } from "./types";

/**
 * Role edit page
 */
export function RolesEdit() {
  const go = useGo();
  const { id } = useParams<{ id: string }>();

  // Load role
  const { result: roleResult, query: roleQuery } = useOne<Role>({
    resource: "roles",
    id: Number(id),
  });

  const role = roleResult;
  const roleLoading = roleQuery.isLoading;

  // Load permissions
  const { result: permissionsResult, query: permissionsQuery } =
    useList<PermissionOption>({
      resource: "permissions",
      pagination: { currentPage: 1, pageSize: 200, mode: "server" },
    });

  const permissions = permissionsResult.data ?? [];
  const permissionsLoading = permissionsQuery.isLoading;

  // Update mutation
  const { mutate: updateMutate, mutation: updateMutationState } = useUpdate();

  const isPending = updateMutationState.isPending;
  const isLoading = roleLoading || permissionsLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!role) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Роль не найдена</p>
        <Button variant="link" onClick={() => go({ to: "/roles" })}>
          Вернуться к списку
        </Button>
      </div>
    );
  }

  const initialFormData = role
    ? {
        displayName: role.displayName,
        description: role.description ?? "",
        permissions: role.permissions ?? [],
      }
    : {
        displayName: "",
        description: "",
        permissions: [],
      };

  const formKey = `role-${role?.id ?? "loading"}`;

  return (
    <RolesEditFormFields
      key={formKey}
      role={role}
      permissions={permissions}
      isPending={isPending}
      initialFormData={initialFormData}
      onSubmit={(values) =>
        updateMutate(
          {
            resource: "roles",
            id: Number(id),
            values: {
              displayName: values.displayName,
              description: values.description,
              permissions: values.permissions,
            },
          },
          {
            onSuccess: () => {
              // Очищаем кэш прав чтобы изменения применились сразу
              clearPermissionsCache();
              go({ to: "/roles" });
            },
          }
        )
      }
      onBack={() => go({ to: "/roles" })}
    />
  );
}

interface RolesEditFormFieldsProps {
  role: Role;
  permissions: PermissionOption[];
  isPending: boolean;
  initialFormData: RolePayload;
  onSubmit: (values: RolePayload) => void;
  onBack: () => void;
}

function RolesEditFormFields({
  role,
  permissions,
  isPending,
  initialFormData,
  onSubmit,
  onBack,
}: RolesEditFormFieldsProps) {
  const [formData, setFormData] = useState<RolePayload>(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.displayName.trim()) {
      newErrors.displayName = "Название обязательно";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {return;}
    onSubmit(formData);
  };

  const togglePermission = (permissionCode: string) => {
    setFormData((prev) => {
      const codes = prev.permissions ?? [];
      if (codes.includes(permissionCode)) {
        return {
          ...prev,
          permissions: codes.filter((code) => code !== permissionCode),
        };
      }
      return { ...prev, permissions: [...codes, permissionCode] };
    });
  };

  const toggleCategory = (categoryCodes: string[]) => {
    const allSelected = categoryCodes.every((code) =>
      formData.permissions?.includes(code)
    );

    setFormData((prev) => {
      if (allSelected) {
        return {
          ...prev,
          permissions: (prev.permissions ?? []).filter(
            (code) => !categoryCodes.includes(code)
          ),
        };
      }
      const existing = new Set(prev.permissions ?? []);
      categoryCodes.forEach((code) => existing.add(code));
      return { ...prev, permissions: [...existing] };
    });
  };

  return (
    <div className="space-y-6">
      <FormHeader
        title="Редактировать роль"
        description={role.displayName}
        onBack={onBack}
        sticky
      />

      <form onSubmit={handleSubmit} className="relative">
        <div className="grid gap-6 max-w-4xl pb-24">
          <FormCard
            title="Основная информация"
            description="Название и описание роли"
          >
            <div className="space-y-4">
              <FormField
                label="Название"
                name="displayName"
                required
                error={errors.displayName}
              >
                <Input
                  id="displayName"
                  value={formData.displayName}
                  onChange={(e) =>
                    setFormData({ ...formData, displayName: e.target.value })
                  }
                  disabled={isPending}
                />
              </FormField>

              <FormField label="Описание" name="description">
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  disabled={isPending}
                  rows={3}
                />
              </FormField>
            </div>
          </FormCard>

          <FormCard
            title="Права доступа"
            description="Выберите права для этой роли"
          >
            <PermissionsSelector
              permissions={permissions}
              selectedCodes={formData.permissions ?? []}
              onTogglePermission={togglePermission}
              onToggleCategory={toggleCategory}
              isDisabled={isPending}
            />
          </FormCard>
        </div>

        <FormSpacer />

        <FormStickyActions
          primaryLabel="Сохранить изменения"
          secondaryLabel="Отмена"
          onSecondary={onBack}
          isPending={isPending}
          pendingLabel="Сохранение..."
          secondaryVariant="secondary"
        />
      </form>
    </div>
  );
}
